// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import deleteQuote from "@adl/App/Handlers/CustomQuote/DeleteCustomQuote";
import customQuoteRename from "@adl/App/Handlers/CustomQuote/RenameCustomQuote";
import { CustomQuoteModel } from "@adl/App/Services/CustomQuotesService";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { rfqMyQuotesHeaderStyles } from "@adl/App/Widgets/MyCustomQuotes/CreateNewCustomQuote";
import { generateButtonLinkStyle, pageTitleStyle } from "@adl/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Edit from "@insite/mobius/Icons/Edit";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { markForFocusLater } from "@insite/mobius/Overlay/helpers/focusManager";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    quote: state.xMyCustomQuoteDetails.quote as CustomQuoteModel,
    myCustomQuotesUrl: getPageLinkByPageType(state, "CustomQuotesPage")?.url,
    language: state.context.session.language,
    isLoading: state.xMyCustomQuoteDetails.isLoading,
});

const mapDispatchToProps = {
    deleteQuote,
    customQuoteRename,
};

type Props = WidgetProps & HasHistory & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyQuotesDetailsActionStyles {
    wrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    nameText?: TypographyProps;
    overflowMenu?: OverflowMenuPresentationProps;
    editButton?: ButtonPresentationProps;
    editQuoteModal?: ModalPresentationProps;
    deleteQuoteModal?: TwoButtonModalStyles;
    lastUpdateWrapper?: InjectableCss;
    lastUpdateText?: TypographyProps;
}

export const actionStyles: MyQuotesDetailsActionStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            white-space: nowrap;
        `,
    },
    nameText: pageTitleStyle,
    editButton: generateButtonLinkStyle(Edit),
    editQuoteModal: {
        sizeVariant: "small",
    },
    overflowMenu: {
        cssOverrides: {
            wrapper: css`
                order: 1;
                margin-left: 5px;
            `,
        },
    },
    lastUpdateWrapper: {
        css: css`
            width: 100%;
            display: block;
        `,
    },
    lastUpdateText: {
        size: 14,
    },
};

const styles = actionStyles;

const editModelStyles = rfqMyQuotesHeaderStyles;

const MyCustomQuoteDetailsHeader = ({
    quote,
    history,
    myCustomQuotesUrl,
    language,
    deleteQuote,
    isLoading,
    customQuoteRename,
}: Props) => {
    if (!quote || isLoading) {
        return null;
    }

    const toasterContext = useContext(ToasterContext);
    const [isUpdateQuoteModalOpen, setIsUpdateQuoteModalOpen] = useState(false);
    const [isDeleteQuoteModalOpen, setIsDeleteQuoteModalOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [newQuoteName, setNewQuoteName] = useState(quote.name);
    const [newQuoteNameError, setNewQuoteNameError] = useState("");

    useEffect(() => {
        setNewQuoteName(quote.name);
    }, [quote.name]);

    const editClickHandler = () => {
        setIsUpdateQuoteModalOpen(true);
    };

    const editCloseHandler = () => {
        setIsUpdateQuoteModalOpen(false);
    };

    const deleteClickHandler = () => {
        setIsDeleteQuoteModalOpen(true);
    };

    const deleteCancelHandler = () => {
        setIsDeleteQuoteModalOpen(false);
    };

    const deleteSubmitHandler = () => {
        if (quote) {
            deleteQuote({
                quoteId: quote.id,
                onSuccess(result: any) {
                    onDeleteSuccess();
                },
            });
        }
    };

    const onDeleteSuccess = () => {
        deleteCancelHandler();
        if (myCustomQuotesUrl) {
            history.push(myCustomQuotesUrl);
        }
    };

    const getModifiedOnDate = (date: string) => {
        const updatedOnDisplay = getLocalizedDateTime({
            dateTime: new Date(date),
            language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
        return updatedOnDisplay;
    };

    const inputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (newQuoteNameError) {
            setNewQuoteNameError("");
        }
        setNewQuoteName(event.target.value);
    };

    const editQuote = () => {
        if (newQuoteName === "") {
            setNewQuoteNameError(translate("Quote Name is Required"));
            return;
        }
        setIsUpdating(true);

        customQuoteRename({
            customQuoteId: quote.id,
            newName: newQuoteName,
            onSuccess: result => {
                onSubmitSuccess();
            },
            onError: error => {
                onSubmitError(error);
            },
        });
    };

    const onSubmitSuccess = () => {
        setIsUpdating(false);
        editCloseHandler();
        toasterContext.addToast({
            body: translate("Quote Name Updated"),
            messageType: "success",
        });
    };

    const onSubmitError = (errorMessage: string) => {
        setIsUpdating(false);
        setNewQuoteNameError(errorMessage);
    };

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.nameText} data-test-selector="customQuoteName">
                    {quote.name}
                </Typography>
                <StyledWrapper {...styles.buttonWrapper} data-test-selector="menuWrapper">
                    <OverflowMenu onOpen={markForFocusLater} {...styles.overflowMenu}>
                        <Clickable onClick={deleteClickHandler} data-test-selector="deleteQuote">
                            {translate("Delete")}
                        </Clickable>
                    </OverflowMenu>
                    <Button {...styles.editButton} onClick={editClickHandler} data-test-selector="editQuote">
                        {translate("Edit")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.lastUpdateWrapper}>
                <Typography {...styles.lastUpdateText}>
                    {new Date().toDateString() === new Date(quote.createdOn).toDateString()
                        ? translate("Created On {0}", getModifiedOnDate(quote.createdOn))
                        : translate("Updated On {0}", getModifiedOnDate(quote.modifiedOn))}
                </Typography>
                &nbsp;
                <Typography {...styles.lastUpdateText}>{translate("By {0}", quote.modifiedBy)}</Typography>
            </StyledWrapper>
            <Modal
                size={500}
                headline={translate("Edit Custom Quote")}
                isOpen={isUpdateQuoteModalOpen}
                handleClose={editCloseHandler}
                {...styles.editQuoteModal}
            >
                <LoadingOverlay loading={isUpdating}>
                    <GridContainer {...editModelStyles.container}>
                        <GridItem {...editModelStyles.textGridItem}>
                            <TextField
                                label={translate("Quote Name")}
                                name="name"
                                value={newQuoteName}
                                error={newQuoteNameError}
                                onChange={inputChangeHandler}
                                required={true}
                            />
                        </GridItem>
                        <GridItem {...editModelStyles.buttonsGridItem}>
                            <Button {...editModelStyles.cancelButton} onClick={editCloseHandler}>
                                {translate("Cancel")}
                            </Button>
                            <Button {...editModelStyles.submitButton} onClick={editQuote}>
                                {translate("Rename")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </LoadingOverlay>
            </Modal>
            <TwoButtonModal
                headlineText={translate("Delete Quote")}
                {...styles.deleteQuoteModal}
                modalIsOpen={isDeleteQuoteModalOpen}
                messageText={`${translate("Are you sure you want to delete")} ${quote.name}?`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                submitTestSelector="submitDeleteCustomQuote"
            ></TwoButtonModal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(MyCustomQuoteDetailsHeader)),
    definition: {
        group: "Custom Quote Details" as any,
        displayName: "Header",
        allowedContexts: ["CustomQuoteDetailsPage" as any],
    },
};

export default widgetModule;
